/**
 * New grid rows, plus the indexes of the rows whose `GroupName` changed. The rows passed in are
 * left untouched.
 * @typedef {{ editedTags: object[], changedIndices: number[] }} GroupEdit
 */

const GROUP_SEPARATOR = ";";

/**
 * The group names in a tag's `GroupName`, trimmed, without empty names or repeats.
 * @param {string} [groupName]
 * @returns {string[]}
 */
export const parseGroupNames = (groupName) =>
  groupName
    ? [
        ...new Set(
          groupName
            .split(GROUP_SEPARATOR)
            .map((name) => name.trim())
            .filter((name) => name.length > 0)
        ),
      ]
    : [];

const groupsOf = (tag) => parseGroupNames(tag.GroupName);

const withoutGroup = (groups, groupName) =>
  groups.includes(groupName) ? groups.filter((g) => g !== groupName) : null;

/** Rewrites `GroupName` on each tag for which `edit` returns a group list rather than null. */
const editGroups = (editedTags, edit) => {
  const changedIndices = [];
  const nextEditedTags = editedTags.map((tag, index) => {
    const groups = edit(groupsOf(tag), index);
    if (!groups) {
      return tag;
    }
    changedIndices.push(index);
    return { ...tag, GroupName: [...new Set(groups)].join(GROUP_SEPARATOR) };
  });
  return { editedTags: nextEditedTags, changedIndices };
};

/**
 * Adds a group to the tags at `indices`, skipping tags already in it.
 * @param {object[]} editedTags Rows as shown in the grid.
 * @param {Iterable<number>} indices
 * @param {string} groupName
 * @returns {GroupEdit}
 */
export const addTagsToGroup = (editedTags, indices, groupName) => {
  const targets = new Set(indices);
  return editGroups(editedTags, (groups, index) =>
    targets.has(index) && !groups.includes(groupName)
      ? [...groups, groupName]
      : null
  );
};

/**
 * @param {object[]} editedTags Rows as shown in the grid.
 * @param {number} tagIndex
 * @param {string} groupName
 * @returns {GroupEdit}
 */
export const removeTagFromGroup = (editedTags, tagIndex, groupName) =>
  editGroups(editedTags, (groups, index) =>
    index === tagIndex ? withoutGroup(groups, groupName) : null
  );

/**
 * Removes a group from every tag in it.
 * @param {object[]} editedTags Rows as shown in the grid.
 * @param {string} groupName
 * @returns {GroupEdit}
 */
export const deleteGroup = (editedTags, groupName) =>
  editGroups(editedTags, (groups) => withoutGroup(groups, groupName));

/**
 * @param {object[]} editedTags Rows as shown in the grid.
 * @param {string} oldGroupName
 * @param {string} newGroupName
 * @returns {GroupEdit}
 */
export const renameGroup = (editedTags, oldGroupName, newGroupName) =>
  editGroups(editedTags, (groups) =>
    groups.includes(oldGroupName)
      ? groups.map((g) => (g === oldGroupName ? newGroupName : g))
      : null
  );
